/**
 * Menu Component - Constants & Configuration
 * ============================================
 *
 * Constantes y tipos para el componente Menu.
 * Un menú de navegación versátil y reutilizable.
 */

import { getIcon } from '../../icons';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Variantes de color
 */
export type MenuVariant = 'default' | 'prime' | 'dark' | 'transparent';

/**
 * Tamaños disponibles
 */
export type MenuSize = 'sm' | 'md' | 'lg';

/**
 * Orientación del menú
 */
export type MenuOrientation = 'horizontal' | 'vertical';

/**
 * Tipos de icono predefinidos
 */
export type MenuIconType =
  | 'home' | 'calendar' | 'trophy' | 'user' | 'settings'
  | 'bell' | 'search' | 'heart' | 'star' | 'logout'
  | 'menu' | 'close' | 'chevron-right' | 'chevron-down'
  | 'court' | 'match' | 'class' | 'prime' | 'help' | 'none';

/**
 * Item del menú
 */
export interface MenuItem {
  id: string;
  label: string;
  icon?: MenuIconType;
  badge?: string | number;
  badgeVariant?: 'default' | 'primary' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  divider?: boolean;
  children?: MenuItem[];
  href?: string;
  active?: boolean;
}

/**
 * Estilos de variante
 */
export interface VariantStyles {
  container: string;
  item: string;
  itemHover: string;
  itemActive: string;
  icon: string;
  label: string;
  badge: string;
  divider: string;
}

/**
 * Estilos de tamaño
 */
export interface SizeStyles {
  container: string;
  item: string;
  icon: string;
  label: string;
  badge: string;
  gap: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Variantes de color
 */
export const VARIANTS: Record<MenuVariant, VariantStyles> = {
  default: {
    container: 'ec-bg-color-surface ec-border ec-border-color-border ec-shadow-sm ec-rounded-2xl',
    item: 'ec-text-color-text',
    itemHover: 'hover:ec-bg-color-bg hover:ec-scale-[1.02]',
    itemActive: 'ec-bg-color-primary-light ec-text-color-primary ec-font-semibold ec-shadow-sm',
    icon: 'ec-text-color-text-muted',
    label: 'ec-text-color-text ec-font-medium',
    badge: 'ec-bg-color-border ec-text-color-text',
    divider: 'ec-border-color-border'
  },
  prime: {
    container: 'ec-bg-gradient-to-br ec-from-prime ec-via-prime ec-to-prime-dark ec-shadow-xl ec-rounded-2xl',
    item: 'ec-text-white/90',
    itemHover: 'hover:ec-bg-white/10 hover:ec-backdrop-blur-sm hover:ec-scale-[1.02]',
    itemActive: 'ec-bg-white/20 ec-text-white ec-font-semibold ec-shadow-lg ec-backdrop-blur-sm',
    icon: 'ec-text-white/80',
    label: 'ec-text-white/95 ec-font-medium',
    badge: 'ec-bg-white/25 ec-text-white ec-backdrop-blur-sm',
    divider: 'ec-border-white/20'
  },
  dark: {
    container: 'ec-bg-gradient-to-b ec-from-gray-900 ec-to-gray-950 ec-shadow-xl ec-rounded-2xl ec-border ec-border-gray-800',
    item: 'ec-text-gray-300',
    itemHover: 'hover:ec-bg-gray-800/80 hover:ec-scale-[1.02]',
    itemActive: 'ec-bg-gradient-to-r ec-from-color-primary ec-to-color-primary-dark ec-text-white ec-font-semibold ec-shadow-lg',
    icon: 'ec-text-gray-400',
    label: 'ec-text-gray-300 ec-font-medium',
    badge: 'ec-bg-gray-800 ec-text-gray-200 ec-border ec-border-gray-700',
    divider: 'ec-border-gray-800'
  },
  transparent: {
    container: 'ec-bg-transparent',
    item: 'ec-text-color-text',
    itemHover: 'hover:ec-bg-color-bg hover:ec-scale-[1.02]',
    itemActive: 'ec-bg-color-primary-light ec-text-color-primary ec-font-semibold ec-shadow-sm',
    icon: 'ec-text-color-text-muted',
    label: 'ec-text-color-text ec-font-medium',
    badge: 'ec-bg-color-border ec-text-color-text',
    divider: 'ec-border-color-border'
  }
};

/**
 * Tamaños
 */
export const SIZES: Record<MenuSize, SizeStyles> = {
  sm: {
    container: 'ec-p-2 ec-rounded-2xl',
    item: 'ec-px-3 ec-py-2 ec-rounded-lg ec-text-sm',
    icon: 'ec-w-4 ec-h-4',
    label: 'ec-text-sm',
    badge: 'ec-text-xs ec-px-2 ec-py-0 ec-min-w-[20px] ec-text-center',
    gap: 'ec-gap-2'
  },
  md: {
    container: 'ec-p-3 ec-rounded-2xl',
    item: 'ec-px-4 ec-py-3 ec-rounded-lg ec-text-sm',
    icon: 'ec-w-5 ec-h-5',
    label: 'ec-text-sm',
    badge: 'ec-text-xs ec-px-3 ec-py-1 ec-min-w-[24px] ec-text-center',
    gap: 'ec-gap-3'
  },
  lg: {
    container: 'ec-p-4 ec-rounded-2xl',
    item: 'ec-px-4 ec-py-3 ec-rounded-lg ec-text-base',
    icon: 'ec-w-6 ec-h-6',
    label: 'ec-text-base',
    badge: 'ec-text-sm ec-px-3 ec-py-1 ec-min-w-[28px] ec-text-center',
    gap: 'ec-gap-4'
  }
};

/**
 * Badge variants
 */
export const BADGE_VARIANTS: Record<string, string> = {
  default: 'ec-bg-gray-200 ec-text-gray-700 ec-font-semibold',
  primary: 'ec-bg-color-primary ec-text-white ec-font-semibold ec-shadow-sm',
  danger: 'ec-bg-danger ec-text-white ec-font-semibold ec-shadow-sm',
  success: 'ec-bg-success ec-text-white ec-font-semibold ec-shadow-sm',
  warning: 'ec-bg-warning ec-text-white ec-font-semibold ec-shadow-sm'
};

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (icon="trophy", etc.) se mantienen igual — cero breaking.
 * Los valores ahora vienen del registro central de íconos Figma
 * (`src/icons`); los sin equivalente Figma usan su SVG legacy vía
 * `getIcon()` (resuelve LEGACY_ICONS automáticamente).
 */
export const ICONS: Record<MenuIconType, string> = {
  home: getIcon('home') || '',
  calendar: getIcon('calendar-empty') || '',
  trophy: getIcon('trophy') || '',
  user: getIcon('user') || '',
  settings: getIcon('settings') || '',
  bell: getIcon('bell') || '',
  search: getIcon('search') || '',
  heart: getIcon('heart') || '',
  star: getIcon('star') || '',
  logout: getIcon('logout') || '',
  menu: getIcon('menu') || '',
  close: getIcon('close') || '',
  'chevron-right': getIcon('chevron-right') || '',
  'chevron-down': getIcon('chevron-down') || '',
  court: getIcon('court') || '',
  match: getIcon('match') || '',
  class: getIcon('class') || '',
  prime: getIcon('prime') || '',
  help: getIcon('question') || '',
  none: ''
};

/**
 * Valores por defecto
 */
export const DEFAULTS = {
  variant: 'default' as MenuVariant,
  size: 'md' as MenuSize,
  orientation: 'vertical' as MenuOrientation,
  collapsible: false,
  showIcons: true
};
